import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { MultiSelect } from "primereact/multiselect";
import { useNavigate } from "react-router-dom";
import { getAllCustomers } from "../../../services/clearing/customers";
import Loader from "../../components/Loader";
import ReusableList from "../../../components/ReusableList";
import AddStation from "./AddStation/AddStation";
import {
  VAT_ID_EXCLUSION_STATUS_FIELD,
  availableColumns,
  defaultColumnValues,
} from "./CustomerList.consts";
import {
  VatIdExclusion,
  exclusionStatusLabel,
} from "../../../utils/vatIdExclusion";

interface Customer {
  id: string; // The service returns objects with IDs, ensuring compatibility
  account_holder_name: string;
  iban: string;
  email: string;
  address: {
    city: string;
    country: string;
    recipient_name?: string;
    company_name?: string;
    street_address?: string;
    postal_code?: string;
  };
  industry?: string;
  phone?: string;
  bic?: string;
  vat_id?: string;
  business_type?: string;
  vat_id_exclusion?: VatIdExclusion | null;
}

const EXCLUSION_COLUMN_ADDED_KEY = "customer_list_columns_vat_id_exclusion_added";

/**
 * A saved column choice predates the VAT ID Exclusion column (CLR-273), so it is added once to
 * show it to everyone; hiding it afterwards sticks, because the migration never runs again.
 */
const withExclusionColumnOnce = (saved: string[]): string[] => {
  if (localStorage.getItem(EXCLUSION_COLUMN_ADDED_KEY)) return saved;
  localStorage.setItem(EXCLUSION_COLUMN_ADDED_KEY, "1");
  if (saved.includes(VAT_ID_EXCLUSION_STATUS_FIELD)) return saved;
  const migrated = [...saved, VAT_ID_EXCLUSION_STATUS_FIELD];
  localStorage.setItem("customer_list_columns", JSON.stringify(migrated));
  return migrated;
};

const CustomerList = () => {
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [visibleColumnKeys, setVisibleColumnKeys] = useState<string[]>(() => {
    const savedColumns = localStorage.getItem("customer_list_columns");
    return savedColumns
      ? withExclusionColumnOnce(JSON.parse(savedColumns))
      : defaultColumnValues;
  });

  useEffect(() => {
    setIsLoading(true);
    getAllCustomers()
      .then((data: any) => setCustomers(data))
      .catch((err) => console.error(err))
      .finally(() => setIsLoading(false));
  }, []);

  // A flat, sortable and filterable value for the exclusion column (CLR-273).
  const rows = useMemo(
    () =>
      customers.map((customer) => ({
        ...customer,
        [VAT_ID_EXCLUSION_STATUS_FIELD]: exclusionStatusLabel(
          customer.vat_id_exclusion,
        ),
      })),
    [customers],
  );

  const onColumnToggle = (event: { value: string[] }) => {
    const selectedColumns = event.value;
    setVisibleColumnKeys(selectedColumns);
    localStorage.setItem("customer_list_columns", JSON.stringify(selectedColumns));
    // A choice made while the column exists is current: never migrate it again.
    localStorage.setItem(EXCLUSION_COLUMN_ADDED_KEY, "1");
  };

  if (isLoading) {
    return <Loader />;
  }

  // Derive the column objects from the selected keys, maintaining the order of availability
  const visibleColumns = availableColumns.filter((col) =>
    visibleColumnKeys.some((key) => key === col.value)
  );

  // Column selection handles the view within the table
  const headerControls = (
    <MultiSelect
      value={visibleColumnKeys}
      options={availableColumns}
      optionLabel="label"
      optionValue="value"
      onChange={onColumnToggle}
      placeholder="Select Columns"
      className="w-full md:w-30rem"
      display="chip"
      filter
    />
  );

  // Global actions affect the data or the system state, separate from table view
  const globalActions = (
    <div className="flex flex-column md:flex-row justify-content-end gap-2 mb-1">
      <AddStation
        classes="w-full md:w-12rem"
        customers={customers || []}
      />
      <div className="w-full md:w-12rem flex justify-content-end ">
        <Button
          className="md:w-12rem w-full"
          label="Add Customer"
          icon="pi pi-user-plus"
          outlined
          onClick={() => navigate("/customers/add")}
        />
      </div>
    </div>
  );

  return (
    <div className="customer-list-container">
      <Toast ref={toast} />

      {globalActions}

      <ReusableList
        data={rows}
        loading={false}
        searchable={true}
        filterDisplay="menu"
        actionButtons={headerControls}
        columns={visibleColumns}
        onRowClick={(e: any) => navigate(`/customers/${e?.data?.id}`)}
      />
    </div>
  );
};

export default CustomerList;
